import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IntlShape } from 'react-intl';
import { handleApiException, showNotification } from '../../../../../global/modal/alerts-host/alert-templates';
import { FormFieldProps, InitialData } from '../../../../../../infrastructure/form-viewmodel-controller';
import { AbstractFormViewModelController } from '../../../../../../infrastructure/implementations/form-viewmodel-controller';
import { IPassportUrlRedirectionDAO, IPassportUrlRedirectionDTO } from '../../../../../../api/passport-url-redirection.dao';
import TrackingServiceProvider from '../../../../../../tracking-service/tracking-service-provider';
import { TRACKING_EVENTS } from '../../../../../../tracking-service/tracking-values';
import { urlRedirectionConfigList, UrlRedirectionFieldKey } from './url-redirection-form.config';
import { URL_REDIRECTION_UPDATED_TEXT } from './url-redirection-form.texts';
import { EnablementStatus } from '../../../../../../constants/common.constants';

interface IUrlRedirectionFormProps extends IPassportUrlRedirectionDTO {
    accountId: number;
    eventId: number;
    intl: IntlShape;
    onSaved: (data: IPassportUrlRedirectionDTO) => void;
}

class UrlRedirectionFormVM {
    private static createFormVM(initialData: InitialData): AbstractFormViewModelController {
        return new class extends AbstractFormViewModelController {
            constructor() {
                super(initialData, 'UrlRedirectionFormVM', urlRedirectionConfigList);
            }
        }();
    }

    public readonly formVM: AbstractFormViewModelController;

    @mobx.observable
    private isSaving = false;

    constructor(
        private readonly props: IUrlRedirectionFormProps,
        @IoC.Inject private readonly urlRedirectionDAO: IPassportUrlRedirectionDAO = IoC.Container.get(IPassportUrlRedirectionDAO),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
    ) {
        this.formVM = UrlRedirectionFormVM.createFormVM({
            [UrlRedirectionFieldKey.ENABLED]: !!props.urlRedirectionEnabled,
            [UrlRedirectionFieldKey.URL]: props.redirectUrl || '',
        });
    }

    public get intl(): IntlShape { return this.props.intl; }
    public get saving(): boolean { return this.isSaving; }
    public get enabled(): boolean { return !!this.formVM.getValue(UrlRedirectionFieldKey.ENABLED); }

    public getUrlField = (): FormFieldProps => this.formVM.getFormVmValuePropsByKey(UrlRedirectionFieldKey.URL);

    public toggleEnabled = (): void => {
        this.formVM.setValue(UrlRedirectionFieldKey.ENABLED, !this.enabled);
    };

    public submitForm = async (): Promise<boolean> => {
        if (this.isSaving) {
            return false;
        }
        if (!this.formVM.isVmValid()) {
            this.formVM.setVmTouched();
            return false;
        }
        const data = this.toDTO();
        try {
            mobx.runInAction(() => {
                this.isSaving = true;
            });
            await this.urlRedirectionDAO.updatePassportUrlRedirection({
                accountId: this.props.accountId,
                eventId: this.props.eventId,
                ...data,
            });
            this.props.onSaved(data);
            showNotification(this.intl.formatMessage(URL_REDIRECTION_UPDATED_TEXT));
            this.trackManageUrlRedirection(data);
            return true;
        } catch (e) {
            handleApiException(e);
            return false;
        } finally {
            mobx.runInAction(() => {
                this.isSaving = false;
            });
        }
    };

    private toDTO = (): IPassportUrlRedirectionDTO => ({
        urlRedirectionEnabled: this.enabled,
        redirectUrl: (this.formVM.getValue(UrlRedirectionFieldKey.URL) as string).trim(),
    });

    private trackManageUrlRedirection = (data: IPassportUrlRedirectionDTO): void => {
        this.trackingService.get().track(TRACKING_EVENTS.MANAGE_URL_REDIRECTION, {
            'Status': data.urlRedirectionEnabled ? EnablementStatus.ENABLED_UPPERCASE : EnablementStatus.DISABLED_UPPERCASE,
            'URL': data.redirectUrl,
        });
    };
}

export { UrlRedirectionFormVM, IUrlRedirectionFormProps };